import * as React from "react";
import { ArrowClockwiseIcon, DotsThreeIcon, ShieldCheckIcon, XIcon } from "@phosphor-icons/react";
import { cn } from "cn";

import { Avatar, AvatarFallback, AvatarImage } from "@libs/zapi-ui/ds/components/avatar";
import { Badge } from "@libs/zapi-ui/ds/components/badge";
import { Button } from "@libs/zapi-ui/ds/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@libs/zapi-ui/ds/components/dropdown-menu";
import {
  SearchBar,
  SearchBarActions,
  SearchBarClear,
  SearchBarIcon,
  SearchBarInput
} from "@libs/zapi-ui/ds/components/search-bar";
import { Skeleton } from "@libs/zapi-ui/ds/components/skeleton";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";

type TeamMemberStatus = "active" | "inactive" | "pending" | "expired";

type TeamMember = {
  id: string;
  name: string;
  email: string;
  initials: string;
  avatarUrl?: string;
  /** Display labels, already translated. */
  role: string;
  access?: string;
  projects?: string;
  status: TeamMemberStatus;
  statusLabel: string;
  /** When they joined or were invited, e.g. "Joined Mar 10, 2025". */
  since?: string;
  isYou?: boolean;
};

type TeamMemberAction = "manage" | "resend" | "revoke";

type TeamMemberLabels = {
  list: string;
  member: string;
  role: string;
  status: string;
  you: string;
  manage: string;
  resend: string;
  revoke: string;
  loading: string;
};

const defaultLabels: TeamMemberLabels = {
  list: "Team members",
  member: "Member",
  role: "Role",
  status: "Status",
  you: "You",
  manage: "Manage access",
  resend: "Resend invite",
  revoke: "Cancel invite",
  loading: "Loading"
};

const statusVariant = {
  active: "success",
  inactive: "neutral",
  pending: "warning",
  expired: "destructive"
} as const satisfies Record<TeamMemberStatus, React.ComponentProps<typeof Badge>["variant"]>;

const rowGrid =
  "grid grid-cols-[minmax(0,1fr)_auto_2rem] items-center gap-x-3 md:grid-cols-[minmax(0,1fr)_9rem_8.5rem_2rem] md:gap-x-4";

const isInvitation = (status: TeamMemberStatus) => status === "pending" || status === "expired";

function TeamMemberIdentity({
  member,
  youLabel = defaultLabels.you,
  size = "default",
  className
}: {
  member: Pick<TeamMember, "name" | "email" | "initials" | "avatarUrl" | "isYou" | "status">;
  youLabel?: string;
  size?: "default" | "lg";
  className?: string;
}) {
  const invited = isInvitation(member.status);
  return (
    <div data-slot="team-member-identity" className={cn("flex min-w-0 items-center gap-3", className)}>
      <Avatar size={size} className={cn(invited && "opacity-70")}>
        {member.avatarUrl && <AvatarImage src={member.avatarUrl} alt="" />}
        <AvatarFallback
          className={cn(
            "typo-label-sm",
            invited
              ? "border border-dashed border-greyscale-300 bg-transparent dark:border-greyscale-600"
              : "bg-greyscale-100 text-greyscale-800 dark:bg-greyscale-900 dark:text-greyscale-100"
          )}
        >
          {member.initials}
        </AvatarFallback>
      </Avatar>
      <div className="flex min-w-0 flex-col">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className={cn("truncate text-foreground", size === "lg" ? "typo-label-lg" : "typo-label-md")}>
            {member.name}
          </span>
          {member.isYou && (
            <Badge variant="outline" className="typo-caption">
              {youLabel}
            </Badge>
          )}
        </span>
        <span className="typo-caption truncate text-muted-foreground">{member.email}</span>
      </div>
    </div>
  );
}

function TeamMemberStatusBadge({ status, label }: { status: TeamMemberStatus; label: string }) {
  return (
    <Badge variant={statusVariant[status]} data-status={status}>
      {label}
    </Badge>
  );
}

const defaultActionsLabel = (member: TeamMember) => `Actions for ${member.name}`;

function TeamMemberRow({
  member,
  busy,
  labels,
  actionsLabel,
  onAction
}: {
  member: TeamMember;
  busy?: TeamMemberAction;
  labels: TeamMemberLabels;
  actionsLabel: (member: TeamMember) => string;
  onAction?: (member: TeamMember, action: TeamMemberAction) => void;
}) {
  const invited = isInvitation(member.status);
  const roleCaption = [member.access, member.projects].filter(Boolean).join(" · ");
  const actions: { action: TeamMemberAction; label: string; icon: typeof XIcon; destructive?: boolean }[] = invited
    ? [
        { action: "resend", label: labels.resend, icon: ArrowClockwiseIcon },
        ...(member.status === "pending"
          ? [{ action: "revoke" as const, label: labels.revoke, icon: XIcon, destructive: true }]
          : [])
      ]
    : member.isYou
    ? []
    : [{ action: "manage", label: labels.manage, icon: ShieldCheckIcon }];

  return (
    <li data-slot="team-member-row" data-status={member.status} className={cn(rowGrid, "gap-y-1.5 py-3")}>
      <TeamMemberIdentity member={member} youLabel={labels.you} />
      <div className="col-span-3 row-start-2 flex min-w-0 flex-col ps-11 md:col-span-1 md:row-start-auto md:ps-0">
        <span className="typo-body-sm truncate text-foreground">
          {member.role}
          {roleCaption && <span className="text-muted-foreground md:hidden"> · {roleCaption}</span>}
        </span>
        {roleCaption && (
          <span className="typo-caption hidden truncate text-muted-foreground md:block">{roleCaption}</span>
        )}
      </div>
      <div className="col-start-2 row-start-1 flex min-w-0 flex-col items-end gap-0.5 md:col-start-auto md:row-start-auto md:items-start">
        <TeamMemberStatusBadge status={member.status} label={member.statusLabel} />
        {member.since && (
          <span className="typo-caption hidden truncate text-muted-foreground md:block">{member.since}</span>
        )}
      </div>
      {actions.length > 0 && (
        <div className="col-start-3 row-start-1 flex justify-end md:col-start-auto md:row-start-auto">
          <DropdownMenu>
            <DropdownMenuTrigger
              render={<Button variant="ghost" size="icon-sm" shape="pill" disabled={!!busy} />}
              aria-label={actionsLabel(member)}
            >
              {busy ? <Spinner aria-label={labels.loading} /> : <DotsThreeIcon weight="bold" />}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-44">
              {actions.map((item) => (
                <DropdownMenuItem
                  key={item.action}
                  variant={item.destructive ? "destructive" : "default"}
                  onClick={() => onAction?.(member, item.action)}
                >
                  <item.icon />
                  {item.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </li>
  );
}

function TeamMemberList({
  members,
  busy,
  labels: labelOverrides,
  actionsLabel = defaultActionsLabel,
  empty,
  footer,
  onAction,
  className
}: {
  members: TeamMember[];
  /** The action running for a member, by member id. */
  busy?: Partial<Record<string, TeamMemberAction>>;
  labels?: Partial<TeamMemberLabels>;
  /** The name of a row's menu button, e.g. "Actions for Amira Kovač". */
  actionsLabel?: (member: TeamMember) => string;
  /** Shown instead of the list when there are no members, e.g. an Empty for a search with no matches. */
  empty?: React.ReactNode;
  footer?: React.ReactNode;
  onAction?: (member: TeamMember, action: TeamMemberAction) => void;
  className?: string;
}) {
  const labels = { ...defaultLabels, ...labelOverrides };
  return (
    <div data-slot="team-member-list" className={cn("flex min-w-0 flex-col", className)}>
      {members.length === 0 ? (
        empty
      ) : (
        <>
          <div
            aria-hidden="true"
            className={cn(rowGrid, "typo-overline hidden border-b pb-2 text-muted-foreground md:grid")}
          >
            <span>{labels.member}</span>
            <span>{labels.role}</span>
            <span>{labels.status}</span>
            <span />
          </div>
          <ul aria-label={labels.list} className="flex flex-col divide-y">
            {members.map((member) => (
              <TeamMemberRow
                key={member.id}
                member={member}
                busy={busy?.[member.id]}
                labels={labels}
                actionsLabel={actionsLabel}
                onAction={onAction}
              />
            ))}
          </ul>
        </>
      )}
      {footer && <div className="typo-caption border-t pt-3 text-muted-foreground">{footer}</div>}
    </div>
  );
}

function TeamMemberListSkeleton({ rows = 5, className }: { rows?: number; className?: string }) {
  return (
    <div data-slot="team-member-list-skeleton" aria-busy="true" className={cn("flex flex-col divide-y", className)}>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className={cn(rowGrid, "gap-y-1.5 py-3")}>
          <div className="flex items-center gap-3">
            <Skeleton className="size-8 shrink-0 rounded-full" />
            <div className="flex min-w-0 flex-col gap-1.5">
              <Skeleton className="h-3.5 w-32" />
              <Skeleton className="h-3 w-44 max-w-full" />
            </div>
          </div>
          <div className="col-span-3 row-start-2 flex flex-col gap-1.5 ps-11 md:col-span-1 md:row-start-auto md:ps-0">
            <Skeleton className="h-3.5 w-16" />
            <Skeleton className="hidden h-3 w-24 md:block" />
          </div>
          <Skeleton className="col-start-2 row-start-1 h-5 w-14 rounded-full md:col-start-auto md:row-start-auto" />
          <Skeleton className="col-start-3 row-start-1 size-8 justify-self-end rounded-full md:col-start-auto md:row-start-auto" />
        </div>
      ))}
    </div>
  );
}

function TeamMemberSearch({
  value,
  onValueChange,
  placeholder = "Search members",
  clearLabel = "Clear search",
  className
}: {
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  clearLabel?: string;
  className?: string;
}) {
  return (
    <SearchBar data-slot="team-member-search" className={cn("h-9 w-full sm:w-64", className)}>
      <SearchBarIcon />
      <SearchBarInput
        value={value}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(event) => onValueChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape" && value) {
            event.preventDefault();
            onValueChange("");
          }
        }}
      />
      {value && (
        <SearchBarActions>
          <SearchBarClear aria-label={clearLabel} onClick={() => onValueChange("")} />
        </SearchBarActions>
      )}
    </SearchBar>
  );
}

export {
  TeamMemberIdentity,
  TeamMemberList,
  TeamMemberListSkeleton,
  TeamMemberSearch,
  TeamMemberStatusBadge,
  type TeamMember,
  type TeamMemberAction,
  type TeamMemberLabels,
  type TeamMemberStatus
};
